import type { ResolvedPlace } from '../types/groups';

/**
 * What a place lookup can answer.
 *
 * Three outcomes, deliberately not two. "We looked and there is no such place"
 * and "we could not look" are different facts, and collapsing them is what put a
 * wrong pin on a live guest timeline: the lookup was broken for every query it
 * ever received, the host read "Nothing found" as "this venue does not exist",
 * typed the venue by hand, and shipped a tile whose coordinates still pointed at
 * the previous location.
 */
export type PlaceLookupResult =
  /** A place, with however many facts came back. */
  | { status: 'found'; place: ResolvedPlace; incomplete: boolean }
  /** The lookup ran and matched nothing. */
  | { status: 'no_match' }
  /** The lookup could not run. Says nothing about whether the place exists. */
  | { status: 'unavailable'; reason: 'not_configured' | 'failed' };

/** The API's 200 body, in the shape the endpoint returns it. */
export interface PlaceResolveBody {
  found?: boolean;
  reason?: string | null;
  tripsby_location_id?: string | null;
  google_place_id?: string | null;
  name?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  business_phone?: string | null;
  website_url?: string | null;
  maps_url?: string | null;
}

export function placeLookupResult(data: PlaceResolveBody | null | undefined): PlaceLookupResult {
  if (!data?.found) return { status: 'no_match' };
  return {
    status: 'found',
    // The venue resolved but the facts behind it did not. Worth admitting
    // rather than presenting a place with no address as complete.
    incomplete: data.reason === 'detail_unavailable',
    place: {
      tripsbyLocationId: data.tripsby_location_id ?? null,
      googlePlaceId: data.google_place_id ?? null,
      name: data.name ?? null,
      address: data.address ?? null,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
      businessPhone: data.business_phone ?? null,
      websiteUrl: data.website_url ?? null,
      mapsUrl: data.maps_url ?? null,
    },
  };
}

/**
 * A thrown request becomes an outcome, never a miss.
 *
 * 503 is the picker being switched off in this environment; everything else is
 * an outage. Neither is evidence about the venue.
 */
export function placeLookupFailure(error: unknown): PlaceLookupResult {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return { status: 'unavailable', reason: status === 503 ? 'not_configured' : 'failed' };
}

/**
 * What the host reads. Each outcome gets its own sentence and its own next step,
 * because the next step differs: retype the name, wait, or fill it in by hand.
 */
export function placeLookupMessage(result: PlaceLookupResult, query: string): string | null {
  switch (result.status) {
    case 'no_match':
      return `We looked and couldn’t find “${query}”. Try the full name, or fill the details in below.`;
    case 'unavailable':
      return result.reason === 'not_configured'
        ? 'Place lookup isn’t switched on here yet — this isn’t about the venue. Fill the details in by hand for now.'
        : 'Place lookup is down, so we couldn’t check whether this venue exists. Try again, or fill the details in by hand.';
    case 'found':
      return result.incomplete
        ? 'Found it, but we couldn’t load its address or map pin. Check the details before saving.'
        : null;
  }
}
